import { handleExpiredTokens } from 'api/apiReqSagaCreator/httpRequest';
import { ApiPaths } from 'api/constants';
import { User } from 'api/endpoints/users';
import { buildBadRequestApiError } from 'container/helpers/errors';
import {
  ApiResponse,
  ApiResponseAsync,
  translateAxiosErrorToApiError,
} from 'container/models';

import BaseRepo from '../../BaseRepo';
import { ApplicationConfiguration } from '../applicationConfiguration/IApplicationConfiguraton';
import {
  EmailContactsSearchRequest,
  IManagementEmailRepo,
  MailContact,
  OrganizationEmailProviderConfig,
  OrganizationEmailProvidersConfig,
  TestEmailPayload,
} from './IManagementEmailRepo';

export class ManagementEmailRepo
  extends BaseRepo
  implements IManagementEmailRepo
{
  searchEmailContacts = (
    emailProvider: OrganizationEmailProviderConfig,
    searchRequest: EmailContactsSearchRequest
  ): ApiResponseAsync<MailContact[]> => {
    const link = emailProvider._links?.['searchEmailProviderContacts'];
    if (!link)
      return Promise.resolve(
        buildBadRequestApiError(
          new Error('link "search email provider contacts" is not availabe')
        )
      );

    const url = new URL(link.href);

    if (searchRequest.displayName) {
      url.searchParams.set('display_name', searchRequest.displayName);
    }
    if (searchRequest.skip) {
      url.searchParams.set('skip', searchRequest.skip.toString());
    }
    if (searchRequest.top) {
      url.searchParams.set('top', searchRequest.top.toString());
    }

    return this.helpers.handleGet(url.toString());
  };
  getReplyTo = (): ApiResponseAsync<string> =>
    handleExpiredTokens(() => this.client.get<User>(ApiPaths.users.info._()))
      .then((response) => response.data.organization.replyTo)
      .catch(translateAxiosErrorToApiError);

  setReplyTo = (email: string): ApiResponseAsync<null> =>
    handleExpiredTokens(() =>
      this.client.put<null>(ApiPaths.replyTo._(), { replyTo: email })
    )
      .then((response) => response.data)
      .catch(translateAxiosErrorToApiError);

  getEmailProviders = (
    user: User
  ): ApiResponseAsync<OrganizationEmailProvidersConfig> => {
    const link = user?._links?.['getOrgEmailProvidersConfig'].href;
    if (!link) {
      return Promise.resolve(
        buildBadRequestApiError(
          new Error('link "get org email providers" is not available')
        )
      );
    }

    return this.helpers.handleGet(link);
  };

  setDefaultEmailProvider = (
    user: User,
    provider: OrganizationEmailProviderConfig
  ): ApiResponseAsync<void> => {
    const link = user?._links?.setDefaultOrgEmailProvider?.href;
    if (!link) {
      return Promise.resolve(
        buildBadRequestApiError(
          new Error('link "set default email provider" is not available')
        )
      );
    }

    return this.helpers.handlePut(link, { emailProviderId: provider.id });
  };

  getConfigurableEmailProviders = (
    user: User
  ): ApiResponseAsync<OrganizationEmailProviderConfig[]> => {
    const link = user?._links?.['getConfiguredOrgEmailProviders'].href;
    if (!link) {
      return Promise.resolve(
        buildBadRequestApiError(
          new Error(
            'link "get configurable org email providers" is not available'
          )
        )
      );
    }

    return this.helpers.handleGet(link);
  };

  setConfigurableEmailProviders = (
    user: User,
    providers: OrganizationEmailProviderConfig[]
  ): ApiResponseAsync<void> => {
    const link = user?._links?.['setConfiguredOrgEmailProviders'].href;
    if (!link) {
      return Promise.resolve(
        buildBadRequestApiError(
          new Error(
            'link "set configurable org email providers" is not available'
          )
        )
      );
    }

    return this.helpers.handlePut(link, providers);
  };

  testEmailServerConfig = async (
    link: Link,
    config: OrganizationEmailProviderConfig,
    email: TestEmailPayload
  ): Promise<ApiResponse<null>> => {
    const content = email.attachmentContent;
    if (content !== undefined && content instanceof Blob) {
      try {
        const b64string: string = await new Promise((resolve, reject) => {
          const fr = new FileReader();
          fr.onload = () => resolve(fr.result as string);
          fr.onerror = reject;
          fr.readAsDataURL(content);
        });

        email.attachmentContent = b64string.split(',')[1];
      } catch {}
    }

    return handleExpiredTokens(() =>
      this.client.post<null>(link.href, { config, email })
    )
      .then((response) => response.data)
      .catch(translateAxiosErrorToApiError);
  };

  createProviderWithOauthLink = async (
    appConfig: ApplicationConfiguration,
    config: OrganizationEmailProviderConfig,
    code: string,
    state: string
  ): ApiResponseAsync<void> => {
    const link = appConfig.emailProviders.postAuthenticate;
    if (!link) {
      return Promise.resolve(
        buildBadRequestApiError(
          new Error('link "email provider postauthenticate" is not available')
        )
      );
    }

    const url = new URL(link);

    url.searchParams.set('code', code);
    url.searchParams.set('state', state);

    return this.helpers.handlePostAction(url.toString(), config);
  };
}
